import React, { useEffect, useState, useMemo } from 'react';
import PageHeader from '../components/PageHeader';
import SkeletonRow from '../components/SkeletonRow';
import { conferenceRoomApi } from '../../../api/adminapi';
import { toast } from 'react-toastify';
import { MdCheck, MdClose, MdSearch, MdEventNote, MdDelete, MdDownload } from 'react-icons/md';
import '../components/ActionModal.css';
import '../Admin.css';
import './BookRoom.css';
import './BookingRequests.css';

/**
 * BookingRequests Component (ADMIN ONLY)
 *
 * Admin can view all booking requests and approve/reject them
 */
function BookingRequests() {
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('pending'); // pending, all
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [actionType, setActionType] = useState(''); // 'approve' or 'reject'
  const [isProcessing, setIsProcessing] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [selectedBookings, setSelectedBookings] = useState([]); // For bulk delete
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Pending status tracking (expired and urgent)
  const [pendingStatus, setPendingStatus] = useState({ expiredBookingIds: [], urgentBookingIds: [] });

  useEffect(() => {
    fetchAllBookingRequests();
    fetchPendingStatus();
    // Refresh pending status every minute
    const interval = setInterval(fetchPendingStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchPendingStatus = async () => {
    try {
      const status = await conferenceRoomApi.getPendingBookingsStatus();
      setPendingStatus(status || { expiredBookingIds: [], urgentBookingIds: [] });
    } catch (error) {
      console.error('Error fetching pending status:', error);
    }
  };

  /** Fetch all booking requests (admin view)  */
  
  const fetchAllBookingRequests = async () => {
    try {
      setIsLoading(true);
      const response = await conferenceRoomApi.getAllBookingRequests();
      const bookingsData = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
      setBookings(bookingsData);
    } catch (error) {
      console.error('Error fetching booking requests:', error);
      toast.error('Failed to load booking requests', {
        position: "top-right",
        autoClose: 3000,
      });
      setBookings([]);
    } finally {
      setIsLoading(false);
    }
  };

  

  /** Filter bookings  */


  const filteredBookings = useMemo(() => {
    // First, filter to show only parent bookings or non-recurring bookings
    // This ensures recurring series appear as single request
    let uniqueBookings = bookings.filter(booking => {
      // Show non-recurring bookings (no recurringGroupId)
      if (!booking.recurringGroupId) return true;
      // For recurring bookings, show only parent booking
      return booking.isParentBooking === true;
    });

    // Apply status filter
    if (filterStatus !== 'all') {
      uniqueBookings = uniqueBookings.filter(booking => booking.status === filterStatus.toUpperCase());
    }

    // Apply text search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      uniqueBookings = uniqueBookings.filter(booking => {
        const roomName = (booking.roomName || '').toLowerCase();
        const userEmail = (booking.userEmail || '').toLowerCase();
        const userId = (booking.userId || '').toString().toLowerCase();
        const purpose = (booking.notes || '').toLowerCase();

        return roomName.includes(query) ||
               userEmail.includes(query) ||
               userId.includes(query) ||
               purpose.includes(query);
      });
    }

    // Apply date range filter
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      uniqueBookings = uniqueBookings.filter(booking => {
        const bookingDate = new Date(booking.startTime);
        return bookingDate >= fromDate;
      });
    }

    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      uniqueBookings = uniqueBookings.filter(booking => {
        const bookingDate = new Date(booking.startTime);
        return bookingDate <= toDate;
      });
    }

    return uniqueBookings;
  }, [bookings, filterStatus, searchQuery, dateFrom, dateTo]);

  
   // Format date and time
  
  const formatDateTime = (dateTimeStr) => {
    const date = new Date(dateTimeStr);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  
   // Calculate duration
   
  const calculateDuration = (startTime, endTime) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMs = end - start;
    const diffMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMins / 60);
    const minutes = diffMins % 60;

    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${minutes}m`;
    }
  };

  
   // Open action modal (approve/reject)
   
  const handleActionClick = (booking, action) => {
    setSelectedBooking(booking);
    setActionType(action);
    setShowActionModal(true);
    setRejectionReason('');
  };

  
   // Close action modal
   
  const closeActionModal = () => {
    setShowActionModal(false);
    setSelectedBooking(null);
    setActionType('');
    setRejectionReason('');
  };


   // Confirm action (approve/reject)

  const confirmAction = async () => {
    if (!selectedBooking) return;

    // Check if booking date has passed (for approval only)
    if (actionType === 'approve') {
      const bookingStartTime = new Date(selectedBooking.startTime);
      const currentTime = new Date();

      if (bookingStartTime < currentTime) {
        toast.error('⚠️ Cannot approve this booking! The booking date has already passed.', {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
        });
        closeActionModal();
        return;
      }
    }

    // Validate rejection reason if rejecting
    if (actionType === 'reject' && !rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection', {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    try {
      setIsProcessing(true);

      if (actionType === 'approve') {
        await conferenceRoomApi.approveBooking(selectedBooking.bookingId || selectedBooking.id);
        toast.success('Booking approved successfully', {
          position: "top-right",
          autoClose: 3000,
        });
      } else if (actionType === 'reject') {
        await conferenceRoomApi.rejectBooking(
          selectedBooking.bookingId || selectedBooking.id,
          rejectionReason
        );
        toast.success('Booking rejected', {
          position: "top-right",
          autoClose: 3000,
        });
      }

      // Refresh list
      await fetchAllBookingRequests();
      closeActionModal();
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || `Failed to ${actionType} booking`;
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsProcessing(false);
    }
  };


   // Get status badge class

  const getStatusBadgeClass = (status) => {
    const statusLower = (status || '').toLowerCase();
    return `status-badge status-${statusLower}`;
  };

  // Check if booking is expired (past start time but still pending)
  const isExpiredPending = (booking) => {
    const bookingId = booking.bookingId || booking.id;
    return pendingStatus.expiredBookingIds?.includes(bookingId);
  };

  // Check if booking is urgent (starting within 1 hour)
  const isUrgentPending = (booking) => {
    const bookingId = booking.bookingId || booking.id;
    return pendingStatus.urgentBookingIds?.includes(bookingId);
  };

  // Get row class based on urgency
  const getRowUrgencyClass = (booking) => {
    if (booking.status !== 'PENDING') return '';
    if (isExpiredPending(booking)) return 'row-expired';
    if (isUrgentPending(booking)) return 'row-urgent';
    return '';
  };

  // Handle checkbox selection
  const handleSelectBooking = (bookingId) => {
    setSelectedBookings(prev => {
      if (prev.includes(bookingId)) {
        return prev.filter(id => id !== bookingId);
      } else {
        return [...prev, bookingId];
      }
    });
  };

  // Handle select all checkboxes
  const handleSelectAll = () => {
    if (selectedBookings.length === filteredBookings.length) {
      setSelectedBookings([]);
    } else {
      setSelectedBookings(filteredBookings.map(b => b.bookingId || b.id));
    }
  };

  // Open bulk delete modal
  const handleBulkDelete = () => {
    if (selectedBookings.length === 0) {
      toast.warning('Please select bookings to delete', {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    setShowBulkDeleteModal(true);
  };

  // Confirm bulk delete
  const confirmBulkDelete = async () => {
    try {
      setIsProcessing(true);
      await conferenceRoomApi.bulkDeleteBookings(selectedBookings);
      toast.success(`${selectedBookings.length} booking(s) deleted successfully`, {
        position: "top-right",
        autoClose: 3000,
      });
      setSelectedBookings([]);
      setShowBulkDeleteModal(false);
      await fetchAllBookingRequests();
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || 'Failed to delete bookings';
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const pendingCount = bookings.filter(b => b.status === 'PENDING').length;

  // Download filtered bookings as CSV
  const downloadCSV = () => {
    if (filteredBookings.length === 0) {
      toast.warning('No bookings to export', { position: "top-right", autoClose: 3000 });
      return;
    }

    const headers = [
      'Booking ID', 'User Name', 'User Email', 'Room Name', 'Tower', 'Floor',
      'Start Time', 'End Time', 'Duration', 'Purpose', 'Status', 'Type',
      'Recurring Count', 'Recurring End Date'
    ];

    const rows = filteredBookings.map(booking => [
      booking.bookingId || booking.id || '',
      booking.userName || booking.bookedBy || '',
      booking.userEmail || '',
      booking.roomName || '',
      booking.tower || '',
      booking.floor || '',
      booking.startTime ? new Date(booking.startTime).toLocaleString() : '',
      booking.endTime ? new Date(booking.endTime).toLocaleString() : '',
      calculateDuration(booking.startTime, booking.endTime),
      booking.purpose || '',
      booking.status || 'PENDING',
      booking.recurringGroupId ? 'Recurring' : 'Single',
      booking.recurringBookingsCount || '',
      booking.recurringEndDate ? new Date(booking.recurringEndDate).toLocaleDateString() : ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `booking_requests_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Exported ${filteredBookings.length} booking requests`, { position: "top-right", autoClose: 3000 });
  };

  return (
    <div className="admin-view booking-requests-view">
      <PageHeader
        breadcrumbs={["Community", "Booking Requests"]}
      />

      {/* Compact Header with Filters - Matching Gatemen Style */}
      <div className="page-header-compact">
        <div className="header-left-compact">
          <h2 className="page-title-compact">
            <MdEventNote className="title-icon" />
            Booking Requests
          </h2>
        </div>
        <div className="header-filters-compact">
          {/* Filter Tabs */}
          <div className="filter-tabs-compact">
            <button
              className={`filter-tab-compact ${filterStatus === 'pending' ? 'active' : ''}`}
              onClick={() => setFilterStatus('pending')}
            >
              Pending ({pendingCount})
            </button>
            <button
              className={`filter-tab-compact ${filterStatus === 'all' ? 'active' : ''}`}
              onClick={() => setFilterStatus('all')}
            >
              All Requests
            </button>
          </div>

          {/* Search Input */}
          <div className="input search-compact">
            <span className="icon"><MdSearch /></span>
            <input
              placeholder="Search by room, user, or purpose..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Date Filters */}
          <input
            type="date"
            className="date-input-compact"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            title="From Date"
          />
          <input
            type="date"
            className="date-input-compact"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            title="To Date"
          />

          {/* Clear Filters */}
          {(searchQuery || dateFrom || dateTo) && (
            <button
              className="btn-clear-compact"
              onClick={() => {
                setSearchQuery('');
                setDateFrom('');
                setDateTo('');
              }}
            >
              Clear
            </button>
          )}

          {/* Download CSV */}
          <button
            className="btn-download-compact"
            onClick={downloadCSV}
            title="Download CSV"
          >
            <MdDownload />
            <span>Download</span>
          </button>

          {/* Bulk Delete */}
          {selectedBookings.length > 0 && (
            <button
              className="btn-delete-compact"
              onClick={handleBulkDelete}
            >
              <MdDelete />
              <span>Delete ({selectedBookings.length})</span>
            </button>
          )}
        </div>
      </div>

      {/* Results Count */}
      <div className="results-count-bar">
        Showing <strong>{filteredBookings.length}</strong> of <strong>{bookings.length}</strong> booking{bookings.length !== 1 ? 's' : ''}
      </div>

      {/* Bookings Table/Cards */}
      <div className="bookings-container">
        {isLoading ? (
          <div className="table card">
            <div className="table-head">
              <div className="cell">User</div>
              <div className="cell">Room</div>
              <div className="cell">Date & Time</div>
              <div className="cell">Duration</div>
              <div className="cell">Purpose</div>
              <div className="cell">Status</div>
              <div className="cell text-right">Actions</div>
            </div>
            {Array.from({ length: 5 }).map((_, idx) => (
              <SkeletonRow key={idx} />
            ))}
          </div>
        ) : filteredBookings.length > 0 ? (
          <>
            {/* Desktop Table View */}
            <div className="table card bookings-table-desktop" style={{ overflowX: 'auto' }}>
              <div className="table-head" style={{ minWidth: '1300px' }}>
                <div className="cell" style={{ width: '40px', flexShrink: 0, flexGrow: 0 }}>
                  <input
                    type="checkbox"
                    checked={selectedBookings.length === filteredBookings.length && filteredBookings.length > 0}
                    onChange={handleSelectAll}
                    title="Select All"
                  />
                </div>
                <div className="cell" style={{ width: '90px', flexShrink: 0, flexGrow: 0 }}>Booking ID</div>
                <div className="cell" style={{ width: '180px', flexShrink: 0, flexGrow: 0 }}>User</div>
                <div className="cell" style={{ width: '120px', flexShrink: 0, flexGrow: 0 }}>Room</div>
                <div className="cell" style={{ width: '180px', flexShrink: 0, flexGrow: 0 }}>Date & Time</div>
                <div className="cell" style={{ width: '70px', flexShrink: 0, flexGrow: 0 }}>Duration</div>
                <div className="cell" style={{ flex: 1, minWidth: '100px' }}>Purpose</div>
                <div className="cell" style={{ width: '130px', flexShrink: 0, flexGrow: 0 }}>Type</div>
                <div className="cell" style={{ width: '100px', flexShrink: 0, flexGrow: 0 }}>Status</div>
                <div className="cell actions-cell" style={{ width: '100px', flexShrink: 0, flexGrow: 0 }}>Actions</div>
              </div>
              {filteredBookings.map((booking, idx) => (
                <div className={`table-row ${getRowUrgencyClass(booking)}`} key={booking.bookingId || booking.id || idx} style={{ minWidth: '1300px' }}>
                  <div className="cell" style={{ width: '40px', flexShrink: 0, flexGrow: 0 }}>
                    <input
                      type="checkbox"
                      checked={selectedBookings.includes(booking.bookingId || booking.id)}
                      onChange={() => handleSelectBooking(booking.bookingId || booking.id)}
                    />
                  </div>
                  <div className="cell" style={{ width: '90px', flexShrink: 0, flexGrow: 0 }}>
                    <span className="booking-id-cell" title={booking.bookingId || booking.id}>
                      {(booking.bookingId || booking.id || '').toString().slice(0, 8)}...
                    </span>
                    {isExpiredPending(booking) && <span className="urgency-badge expired">EXPIRED</span>}
                    {isUrgentPending(booking) && <span className="urgency-badge urgent">URGENT</span>}
                  </div>
                  <div className="cell" style={{ width: '180px', flexShrink: 0, flexGrow: 0 }}>
                    <div className="user-info">
                      <strong>{booking.userName || booking.bookedBy || 'Unknown'}</strong>
                      <small>{booking.userEmail || ''}</small>
                    </div>
                  </div>
                  <div className="cell" style={{ width: '120px', flexShrink: 0, flexGrow: 0 }}>
                    <div className="room-info">
                      <strong>{booking.roomName || 'Conference Room'}</strong>
                      <small>{booking.tower || ''} {booking.floor || ''}</small>
                    </div>
                  </div>
                  <div className="cell" style={{ width: '180px', flexShrink: 0, flexGrow: 0 }}>
                    <div className="datetime-info">
                      <div>{formatDateTime(booking.startTime)}</div>
                    </div>
                  </div>
                  <div className="cell" style={{ width: '70px', flexShrink: 0, flexGrow: 0 }}>
                    <span className="duration-badge">
                      {calculateDuration(booking.startTime, booking.endTime)}
                    </span>
                  </div>
                  <div className="cell" style={{ flex: 1, minWidth: '100px' }}>
                    <span className="purpose-text">
                      {booking.purpose || '-'}
                    </span>
                  </div>
                  <div className="cell" style={{ width: '130px', flexShrink: 0, flexGrow: 0 }}>
                    {booking.recurringGroupId ? (
                      <div className="recurring-info" style={{ fontSize: '12px' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          backgroundColor: '#e3f2fd',
                          color: '#1565c0',
                          borderRadius: '4px',
                          fontWeight: '500',
                          marginBottom: '2px'
                        }}>
                          🔁 Recurring
                        </span>
                        <div style={{ color: '#666', fontSize: '11px', marginTop: '2px' }}>
                          {booking.recurringBookingsCount && (
                            <span>{booking.recurringBookingsCount} bookings</span>
                          )}
                          {booking.recurringEndDate && (
                            <div>Till: {new Date(booking.recurringEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        backgroundColor: '#f5f5f5',
                        color: '#666',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}>
                        Single
                      </span>
                    )}
                  </div>
                  <div className="cell" style={{ width: '100px', flexShrink: 0, flexGrow: 0 }}>
                    <span className={getStatusBadgeClass(booking.status)}>
                      {booking.status || 'PENDING'}
                    </span>
                  </div>
                  <div className="cell actions-cell" style={{ width: '100px', flexShrink: 0, flexGrow: 0 }}>
                    {booking.status === 'PENDING' && (
                      <>
                        <button
                          className="btn-action-compact btn-approve"
                          onClick={() => handleActionClick(booking, 'approve')}
                          title="Approve"
                        >
                          <MdCheck />
                        </button>
                        <button
                          className="btn-action-compact btn-reject"
                          onClick={() => handleActionClick(booking, 'reject')}
                          title="Reject"
                        >
                          <MdClose />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile Card View */}
            <div className="bookings-cards-mobile">
              {filteredBookings.map((booking, idx) => (
                <div className="booking-card" key={booking.bookingId || booking.id || idx}>
                  <div className="booking-card-header">
                    <input
                      type="checkbox"
                      checked={selectedBookings.includes(booking.bookingId || booking.id)}
                      onChange={() => handleSelectBooking(booking.bookingId || booking.id)}
                      style={{ marginRight: '10px' }}
                    />
                    <div className="room-name">{booking.roomName || 'Conference Room'}</div>
                    <span className={getStatusBadgeClass(booking.status)}>
                      {booking.status || 'PENDING'}
                    </span>
                  </div>
                  <div className="booking-card-body">
                    <div className="booking-detail">
                      <span className="label">Booking ID:</span>
                      <span className="value booking-id-cell" title={booking.bookingId || booking.id}>
                        {(booking.bookingId || booking.id || '').toString().slice(0, 8)}...
                      </span>
                    </div>
                    <div className="booking-detail">
                      <span className="label">👤 User:</span>
                      <span className="value">{booking.userName || booking.bookedBy || 'Unknown'}</span>
                    </div>
                    <div className="booking-detail">
                      <span className="label">📅 Date & Time:</span>
                      <span className="value">{formatDateTime(booking.startTime)}</span>
                    </div>
                    <div className="booking-detail">
                      <span className="label">⏱️ Duration:</span>
                      <span className="value duration-badge">
                        {calculateDuration(booking.startTime, booking.endTime)}
                      </span>
                    </div>
                    <div className="booking-detail">
                      <span className="label">📍 Location:</span>
                      <span className="value">{booking.tower || ''} {booking.floor || ''}</span>
                    </div>
                    <div className="booking-detail">
                      <span className="label">📝 Purpose:</span>
                      <span className="value">
                        {booking.recurringGroupId && <span title="Recurring Booking Series">🔁 </span>}
                        {booking.purpose || '-'}
                      </span>
                    </div>
                    {booking.recurringGroupId && (
                      <div className="booking-detail" style={{ backgroundColor: '#e8f4fd', padding: '5px', borderRadius: '4px', marginTop: '5px' }}>
                        <span className="label">🔁 Type:</span>
                        <span className="value"><strong>Recurring Series</strong></span>
                      </div>
                    )}
                  </div>
                  {booking.status === 'PENDING' && (
                    <div className="booking-card-footer">
                      <button
                        className="btn-action-compact btn-approve btn-half"
                        onClick={() => handleActionClick(booking, 'approve')}
                      >
                        <MdCheck /> Approve
                      </button>
                      <button
                        className="btn-action-compact btn-reject btn-half"
                        onClick={() => handleActionClick(booking, 'reject')}
                      >
                        <MdClose /> Reject
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="empty-state card">
            <div className="empty-icon">📋</div>
            <h3>No booking requests found</h3>
            <p>
              {filterStatus === 'pending' && "There are no pending booking requests at the moment."}
              {filterStatus === 'all' && "No booking requests have been made yet."}
            </p>
          </div>
        )}
      </div>

      {/* Action Confirmation Modal */}
      {showActionModal && (
        <div className="modal-overlay" onClick={closeActionModal}>
          <div className="modal-content action-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{actionType === 'approve' ? 'Approve' : 'Reject'} Booking</h3>
              <button className="modal-close" onClick={closeActionModal}>×</button>
            </div>
            <div className="modal-body">
              <p>
                {actionType === 'approve'
                  ? 'Are you sure you want to approve this booking request?'
                  : 'Are you sure you want to reject this booking request?'}
              </p>
              {selectedBooking && (
                <>
                  <div className="booking-summary">
                    <div><strong>User:</strong> {selectedBooking.userName || selectedBooking.bookedBy || 'Unknown'}</div>
                    <div><strong>Room:</strong> {selectedBooking.roomName || 'Conference Room'}</div>
                    <div><strong>Date:</strong> {formatDateTime(selectedBooking.startTime)}</div>
                    <div><strong>Duration:</strong> {calculateDuration(selectedBooking.startTime, selectedBooking.endTime)}</div>
                    <div><strong>Purpose:</strong> {selectedBooking.purpose || '-'}</div>
                    {selectedBooking.recurringGroupId && (
                      <div style={{ marginTop: '10px', padding: '12px', backgroundColor: '#e3f2fd', borderRadius: '6px', border: '1px solid #90caf9' }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                          <span style={{ fontSize: '18px', marginRight: '8px' }}>🔁</span>
                          <strong style={{ color: '#1565c0' }}>Recurring Booking Series</strong>
                        </div>
                        <div style={{ fontSize: '13px', color: '#333', marginBottom: '6px' }}>
                          {selectedBooking.recurringBookingsCount && (
                            <div><strong>Total Bookings:</strong> {selectedBooking.recurringBookingsCount}</div>
                          )}
                          {selectedBooking.recurringEndDate && (
                            <div><strong>Series Ends:</strong> {new Date(selectedBooking.recurringEndDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</div>
                          )}
                        </div>
                        <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#d32f2f', fontWeight: '500' }}>
                          ⚠️ This action will {actionType} ALL {selectedBooking.recurringBookingsCount || ''} bookings in this recurring series.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Warning for expired booking */}
                  {actionType === 'approve' && new Date(selectedBooking.startTime) < new Date() && (
                    <div className="expired-booking-warning">
                      <span className="warning-icon">⚠️</span>
                      <div className="warning-content">
                        <strong>Booking Date Has Passed!</strong>
                        <p>This booking is for {formatDateTime(selectedBooking.startTime)}, which has already occurred. You cannot approve expired bookings.</p>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Rejection Reason */}
              {actionType === 'reject' && (
                <div className="rejection-reason-group">
                  <label htmlFor="rejectionReason">Reason for Rejection <span className="required">*</span></label>
                  <textarea
                    id="rejectionReason"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Please provide a reason for rejecting this booking..."
                    rows="4"
                    required
                  />
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button
                className="admin-btn admin-btn-secondary"
                onClick={closeActionModal}
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button
                className={`admin-btn ${actionType === 'approve' ? 'admin-btn-success' : 'admin-btn-danger'}`}
                onClick={confirmAction}
                disabled={isProcessing}
              >
                {isProcessing
                  ? (actionType === 'approve' ? 'Approving...' : 'Rejecting...')
                  : (actionType === 'approve' ? 'Yes, Approve' : 'Yes, Reject')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowBulkDeleteModal(false)}>
          <div className="modal-content action-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Multiple Bookings</h3>
              <button className="modal-close" onClick={() => setShowBulkDeleteModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete {selectedBookings.length} booking(s)?</p>
              <div className="expired-booking-warning" style={{ backgroundColor: '#fff3cd', borderColor: '#ffc107' }}>
                <span className="warning-icon">⚠️</span>
                <div className="warning-content">
                  <strong>Warning!</strong>
                  <p>This action cannot be undone. All selected bookings will be permanently deleted.</p>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="admin-btn admin-btn-secondary"
                onClick={() => setShowBulkDeleteModal(false)}
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button
                className="admin-btn admin-btn-danger"
                onClick={confirmBulkDelete}
                disabled={isProcessing}
              >
                {isProcessing ? 'Deleting...' : `Yes, Delete ${selectedBookings.length} Booking(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BookingRequests;
